import React, { useState, useEffect, useMemo } from 'react';
import { browserVersion, browserName, osName, osVersion } from 'react-device-detect';
import { useNavigate } from 'react-router-dom';
import Path from '@constants/paths';
import { isEmailValid, stringHasValue } from '@services/validation/ValidationRules';
import ContactUsApi from '@apis/contactus.api';
import useGlobalAlert from '@app/core/GlobalAlertModal';
import {
  INVALID_EMAIL_VERBOSE,
  ISSUE_CATEGORY_REQUIRED,
  REASON_REQUIRED,
  FULL_NAME_REQUIRED,
} from '@constants/formField-constants';
import { fireGAEvent } from '@app/core/tracking.service';
import { CONTACT_US__SUBMIT } from '@constants/ga-events.constants';
import { Button, Select, Input, TextArea, Panel } from '@ftdr/blueprint-components-react';
import {
  MediaUploadMicroFrontend,
  FileUpload,
  CustomError,
} from '@ftdr/media-upload-micro-frontend';
import msgs from '@app/locales/en';
import REText from '@components/wrappedBDS/REText';
import { getCurrentThemeConfig } from '@app/core/theme-config';

const REASON = {
  LOGIN: 'Login',
  ORDER: 'Order Management',
  PAYMENTS: 'Payments',
  UPDATE: 'Update Profile or Office',
  WARRANTYLINK: 'WarrantyLink',
  PLACEHOLDER: 'Select One',
  OTHER: 'Other',
};

const reasonList = [
  { value: REASON.ORDER },
  { value: REASON.LOGIN },
  { value: REASON.UPDATE },
  { value: REASON.PAYMENTS },
  { value: REASON.WARRANTYLINK },
  { value: REASON.OTHER },
];

const MAXFILES = 3;
const ACCEPTED_FILE_TYPES =
  'image/*, application/msword, application/pdf, text/plain, application/vnd.openxmlformats-officedocument.wordprocessingml.document';
const EXPLANATION_MAX_LENGTH = 4000;
const FULL_NAME_MAX_LENGTH = 50;

interface IContactUsProps {
  email: string;
  roleType: string;
  fullName: string;
  isAuthed: boolean;
  guestToken?: string;
  contractId?: string;
  topic?: string;
  emailText?: string;
}

const ContactUsTemplate: React.FC<IContactUsProps> = (props: IContactUsProps) => {
  const themeConfig = useMemo(() => getCurrentThemeConfig(), []);
  const navigate = useNavigate();
  const [email, setEmail] = useState(props.email);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [additionalEmail, setAdditionalEmail] = useState('');
  const [fullName, setFullName] = useState(props.fullName);
  const [reason, setReason] = useState(null);
  const [details, setDetails] = useState('');
  const [uploadFiles, setUploadFiles] = useState([]);
  const [isUploadDisabled, setIsUploadDisabled] = useState(false);
  const [showIssueCategoryError, setShowIssueCategoryError] = useState(false);
  const [showExplainIssueError, setShowExplainIssueError] = useState(false);
  const [showEmailError, setShowEmailError] = useState(false);
  const [showAdditionalEmailError, setShowAdditionalEmailError] = useState(false);
  const [showFullNameError, setShowFullNameError] = useState(false);
  const { addErrorToQueue } = useGlobalAlert();
  const [selected, setSelected] = useState({});

  useEffect(() => {
    setEmail(props.email);
    setSelected(reasonList.filter((listElement) => listElement.value === props.topic)[0]);
    setReason(reasonList.filter((listElement) => listElement.value === props.topic)[0]);
    setDetails(props.emailText);
  }, [props.email, props.emailText, props.topic]);

  useEffect(() => {
    setFullName(props.fullName);
  }, [props.fullName]);

  const isEmailInputDisabled = (): boolean => {
    return props.email && props.email.length > 0;
  };

  const handleFaqClick = () => {
    navigate(Path.FAQ);
  };

  const canSubmit = (): boolean => {
    return (
      email.length > 0 &&
      isEmailValid(email) &&
      reason != null &&
      stringHasValue(details) &&
      stringHasValue(fullName) &&
      (additionalEmail.length === 0 || isEmailValid(additionalEmail))
    );
  };

  const setErrorMessages = () => {
    if (!isEmailValid(email)) {
      setShowEmailError(true);
    }
    if (!reason) {
      setShowIssueCategoryError(true);
    }
    if (!stringHasValue(details)) {
      setShowExplainIssueError(true);
    }
    if (!stringHasValue(fullName)) {
      setShowFullNameError(true);
    }
    if (!isEmailValid(additionalEmail) && additionalEmail.length > 0) {
      setShowAdditionalEmailError(true);
    }
  };

  const handleSelect = (selectedReason: any) => {
    setReason(selectedReason);
    setSelected(selectedReason);
    setShowIssueCategoryError(false);
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    if (!canSubmit()) {
      setErrorMessages();
      return;
    }

    if (isSubmitting) return;
    setIsSubmitting(true);

    console.log('submitting contact form');

    const browser = `${browserName} v.${browserVersion}`;
    const os = `${osName} v.${osVersion}`;

    const formData = new FormData();
    formData.append('IssueCategory', reason.value);
    formData.append('Email', email);
    formData.append('ExplainYourIssue', details);
    formData.append('Browser', browser);
    formData.append('Device', os);
    formData.append('UserRole', props.roleType);
    formData.append('AdditionalEmail', additionalEmail);
    formData.append('FullName', fullName);

    let i = 1;
    uploadFiles.forEach((uploadFile) => {
      const key = `File${i.toString()}`;
      formData.append(key, uploadFile.file, uploadFile.file.name);
      i += 1;
    });

    fireGAEvent(CONTACT_US__SUBMIT(props.roleType, reason.value));
    ContactUsApi.submit(formData, props.guestToken)
      .then((result) => {
        if (result === true) {
          navigate(Path.ContactUsSuccess);
        } else {
          addErrorToQueue(msgs.EMAIL_SENDING_ERROR);
          console.error('sending contact us email failed');
        }
      })
      .catch((err) => {
        addErrorToQueue(msgs.EMAIL_SENDING_ERROR);
        console.error('contact us submission error', err);
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const handleMediaUploadFailed = (failedFiles: FileUpload[]) => {
    if (failedFiles && failedFiles.length > 0) {
      console.error('upload of files failed', failedFiles);
    }
  };

  const validateUploadFiles = (files: any): CustomError | null | undefined => {
    const nNewFiles = files.length;
    const nExistingFiles = uploadFiles.length;
    if (nNewFiles + nExistingFiles > MAXFILES) {
      return {
        acceptedFiles: null,
        errorMessages: [`Too many files. Maximum number: ${MAXFILES}`],
      };
    }
    return null;
  };

  const handleMediaUpdateSuccess = (files: FileUpload[]) => {
    console.log('uploaded successfully ', files);

    if (files) {
      const previousUploadFiles = uploadFiles;
      let update = false;

      if (previousUploadFiles.length !== files.length) {
        update = true;
      } else {
        previousUploadFiles.forEach((id) => {
          if (!files.some((el) => el.fileId === id.fileId)) {
            update = true;
          }
        });
      }

      if (update === true) {
        setUploadFiles(files);
        if (files.length >= MAXFILES) {
          setIsUploadDisabled(true);
        } else {
          setIsUploadDisabled(false);
        }
      }
    }
  };

  return (
    <div id="contact-us">
      <Panel
        rounded={themeConfig?.contactUsPanelRounded}
        shadow={themeConfig?.contactUsPanelShadow}
        border={themeConfig?.contactUsPanelBorder}
      >
        <form id="contact-us-form" onSubmit={handleSubmit}>
          <div className={themeConfig?.contactUsFormClass}>
            <REText variant="heading-05" className={themeConfig?.contactUsHeaderClass}>
              Submit a Support Request
            </REText>
            <div id="contact-us-faq" className={themeConfig?.contactUsFormFaqClass}>
              Did you check our{' '}
              <u>
                <a
                  id="contact-us-faq-url"
                  className={themeConfig?.contactUsFormFaqUrlClass}
                  onClick={handleFaqClick}
                >
                  FAQ
                </a>
              </u>
              ? Your question may be answered there.
            </div>

            <div className="text-sm flex flex-col gap-y-4">
              <Select
                formField={true}
                id="contact-reason-select"
                label="Issue Category"
                placeholder={REASON.PLACEHOLDER}
                options={reasonList}
                selected={selected}
                onSelect={handleSelect}
                error={showIssueCategoryError ? ISSUE_CATEGORY_REQUIRED : null}
                color="primary"
                required={true}
              />
              <Input
                formField={true}
                id="contact-us-fullname-input"
                disabled={props.isAuthed}
                label="Name"
                placeholder="Enter Full Name"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                onBlur={(e) => {
                  const trimmed = e.target.value?.trim();
                  setFullName(trimmed);
                  setShowFullNameError(!stringHasValue(trimmed));
                }}
                showClearButton
                maxLength={FULL_NAME_MAX_LENGTH}
                error={showFullNameError ? FULL_NAME_REQUIRED : null}
                required={true}
              />
              <Input
                formField={true}
                id="contact-us-email-input"
                disabled={isEmailInputDisabled()}
                label={props.isAuthed ? 'Account Email Address' : 'Email Address'}
                placeholder="user@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onBlur={(e) => {
                  const trimmed = e.target.value?.trim();
                  setEmail(trimmed);
                  setShowEmailError(!isEmailValid(trimmed));
                }}
                showClearButton
                error={showEmailError ? INVALID_EMAIL_VERBOSE : null}
                required={true}
              />
              {props.isAuthed && (
                <>
                  <Input
                    formField={true}
                    id="contact-us-additional-email-input"
                    label="Additional Email Address"
                    placeholder="user@example.com"
                    value={additionalEmail}
                    onChange={(e) => setAdditionalEmail(e.target.value)}
                    onBlur={(e) => {
                      const trimmed = e.target.value?.trim();
                      setAdditionalEmail(trimmed);
                      setShowAdditionalEmailError(!isEmailValid(trimmed) && trimmed.length > 0);
                    }}
                    showClearButton
                    error={showAdditionalEmailError ? INVALID_EMAIL_VERBOSE : null}
                  />
                </>
              )}
              <TextArea
                formField={true}
                id="contact-us-explanation-input"
                label="Please Explain Your Issue"
                labelProps={{ color: 'red' }}
                textAreaClassName="text-lg"
                value={details}
                placeholder="Share details to make it easier for us to help you."
                rows={7}
                textLength={{
                  current: details?.length,
                  max: EXPLANATION_MAX_LENGTH,
                }}
                onChange={(e) => setDetails(e.target.value)}
                onBlur={(e) => {
                  const trimmed = e.target.value?.trim();
                  setDetails(trimmed);
                  setShowExplainIssueError(!trimmed.length);
                }}
                error={showExplainIssueError ? REASON_REQUIRED : null}
                required={true}
              />

              <div id="contact-us-upload-title" className={themeConfig?.contactUsUploadTitleClass}>
                <b>Upload a screenshot of the issue</b> (optional)
              </div>
              <div className={themeConfig?.contactUsUploadClass}>
                <div>
                  <MediaUploadMicroFrontend
                    id="contact-us-upload-microfrontend"
                    className="height: 100% color:primary"
                    formFieldClassName="height: 100%  color:primary"
                    baseURL={null}
                    token={null}
                    isLocalOnly={true}
                    label=""
                    maxFiles={MAXFILES}
                    multiple={true}
                    dragAndDrop={true}
                    formField={true}
                    accept={ACCEPTED_FILE_TYPES}
                    disabled={isUploadDisabled}
                    customValidator={validateUploadFiles}
                    onMediaFailedErrors={handleMediaUploadFailed}
                    onMediaUpdate={handleMediaUpdateSuccess}
                  />
                </div>
                <div className="py-6">
                  <Button
                    width="full"
                    id="submit-contact-us"
                    type="submit"
                    color={themeConfig?.contactUsSubmitButtonColor}
                    shape={themeConfig?.contactUsSubmitButtonShape}
                    disabled={isSubmitting}
                    size="medium"
                    label={isSubmitting ? 'Submitting...' : 'Submit Request'}
                  />
                </div>
              </div>
            </div>
          </div>
        </form>
      </Panel>
    </div>
  );
};

export default ContactUsTemplate;
